import { DateTime } from "luxon";

import { ILead } from "../../models/Lead";
import { IVehicle, VehicleStatus, YardStatus } from "../../models/Vehicle";
import formatDateTime from "../../utils/formatDateTime";
import useStockNumber from "../../hooks/useStockNumber";
import useLocations from "../../data/useLocations";
import VehicleStatusCell from "./VehicleStatusCell";

const Stat = ({ label, value }: { label: string; value: string }) => (
  <div className="flex flex-col">
    <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">{label}</span>
    <span className="text-sm font-medium text-gray-900">{value}</span>
  </div>
);

export default function VehicleHeaderBar({ vehicle, lead }: { vehicle: IVehicle; lead?: ILead | null }) {
  const { getStockNumber } = useStockNumber();
  const { locations } = useLocations();

  const yardStatus = vehicle.yardStatus !== YardStatus.None ? YardStatus[vehicle.yardStatus] : "—";

  const daysOnSite = (() => {
    if (!vehicle.collectedOn) return "—";
    const days = Math.max(0, Math.floor(DateTime.now().diff(DateTime.fromISO(vehicle.collectedOn), "days").days));
    return `${days} day${days === 1 ? "" : "s"}`;
  })();

  const arrived = vehicle.collectedOn ? formatDateTime(vehicle.collectedOn, DateTime.DATE_SHORT) : "—";
  const location =
    vehicle.yardLocation?.name ??
    locations.data?.data?.find((loc) => loc.id === vehicle.locationId)?.name ??
    "—";
  const source = lead?.source ?? "—";
  const reference = getStockNumber(vehicle) || vehicle.stockNumber || vehicle.vrm || "—";

  return (
    <div className="bg-white shadow rounded-lg p-4 sm:p-6">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-xl font-medium text-gray-900">
          {`${vehicle.vrm ?? ""} ${vehicle.combinedMake ?? ""} ${vehicle.modelRangeDescription ?? ""}`.trim()}
        </h1>
        <div className="w-fit">
          <VehicleStatusCell status={vehicle.status} statusDate={vehicle.statusDate} />
        </div>
      </div>

      <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-7">
        <Stat label="Current Status" value={VehicleStatus[vehicle.status]} />
        <Stat label="Yard Status" value={yardStatus} />
        <Stat label="Days on Site" value={daysOnSite} />
        <Stat label="Location" value={location} />
        <Stat label="Arrived" value={arrived} />
        <Stat label="Source" value={source} />
        <Stat label="Reference" value={reference} />
      </div>
    </div>
  );
}
